import { useChatStore } from "@lib/stores/chatStore";
import { useEffect, useRef } from "react";

/**
 * Calls `onUpdate` whenever the chat agent modifies data via tool execution.
 * Uses the store's `dataVersion` counter which increments after each tool
 * completes.
 *
 * On mount, if `dataVersion > 0` — i.e. the chat has already run at least one
 * tool in this session — fires `onUpdate` once so the subscribing query
 * refetches. This closes the navigate-after-chat gap where a view mounts
 * after the chat has already mutated data and urql's cache-first policy
 * would otherwise serve a pre-chat snapshot. See story #2292.
 */
export function useChatDataVersion(onUpdate: () => void) {
  const dataVersion = useChatStore((s) => s.dataVersion);
  const lastSeen = useRef<number | null>(null);

  useEffect(() => {
    if (lastSeen.current === null) {
      lastSeen.current = dataVersion;
      if (dataVersion > 0) onUpdate();
      return;
    }
    if (dataVersion !== lastSeen.current) {
      lastSeen.current = dataVersion;
      onUpdate();
    }
  }, [dataVersion, onUpdate]);
}
